import { Button, useModal, withModalConditionalRender } from '@billy/design-system'
import { ReactPortal, useCallback, useMemo } from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'

import { mapBillyExternalInvoiceToInvoice } from '@features/invoices/utils/mapBillyExternalInvoiceToInvoice'
import { AccountingBasis } from '@modules-deprecated/app/organization/types/accountingBasis'
import { InvoiceReconciliationModal as InvoiceReconciliationModalComponent } from '@views/invoices/elements/InvoiceReconclliationModal/InvoiceReconciliationModal'

import { ModalId } from '../../../../../../../../enums/modalId'
import { importedInvoicePaymentStatusToVariant } from '../../../../../../constants/importedInvoicePaymentStatusToVariant'
import { ImportedInvoicePaymentFlags } from '../../../../../../types/importedInvoicePaymentFlags'
import { useImportedInvoiceDrawerActionsElement } from '../../../../context/importedInvoiceDrawerActions'

const InvoiceReconciliationModal = withModalConditionalRender(InvoiceReconciliationModalComponent)

interface ImportedInvoicePaymentActionButtonProps {
  paymentFlags: ImportedInvoicePaymentFlags
}

/** Shown in the drawer header. Registers a payment on the Billy invoice; voiding is not wired up yet. */
export const ImportedInvoicePaymentActionButton = ({
  paymentFlags,
}: ImportedInvoicePaymentActionButtonProps): ReactPortal | null => {
  const { t } = useTranslation()
  const actionsElement = useImportedInvoiceDrawerActionsElement()
  const { open: openReconciliationModal } = useModal(ModalId.ImportedInvoiceReconciliationModal)

  const { canRegisterPayment, externalInvoice, isPending, paymentStatus } = paymentFlags
  const reconciliationSubject = useMemo(
    () => (externalInvoice ? mapBillyExternalInvoiceToInvoice(externalInvoice) : undefined),
    [externalInvoice],
  )
  const isCashBasis = externalInvoice?.accountingBasis === AccountingBasis.Cash

  const handleButtonClick = useCallback(() => {
    openReconciliationModal()
  }, [openReconciliationModal])

  if (!actionsElement || !paymentStatus) {
    return null
  }

  const { actionKey, actionVariant } = importedInvoicePaymentStatusToVariant[paymentStatus]

  return createPortal(
    <>
      <Button
        aria-busy={isPending}
        disabled={!canRegisterPayment}
        loading={isPending}
        onClick={handleButtonClick}
        variant={actionVariant}
      >
        {t(actionKey)}
      </Button>
      {reconciliationSubject && (
        <InvoiceReconciliationModal
          id={ModalId.ImportedInvoiceReconciliationModal}
          isCashBasis={isCashBasis}
          subject={reconciliationSubject}
        />
      )}
    </>,
    actionsElement,
  )
}
